











































































































































import { Component, Vue } from 'vue-property-decorator'
import { intersection } from 'lodash'
import api from '@/api'
import store from '@/store'
import { TestFilter } from '@/types/frontend'
import { TestStatus, TestTypes, Test } from '@shared/types/database'
import { orderBy, uniq } from 'lodash-es'
import FilterSVG from '@/assets/img/asset-type-icons/filter.svg?inline'
import TestTable from '@/components/ProjectDetails/TestTable.vue'
import FilterBar, { normalizeSearchTerm } from '@/components/FilterBar.vue'
import AddTestModal from '@/components/AddTest/AddTestModal.vue'
import { Button, Card, TestCard, Icon, Select, SelectOption } from '@alpha/ink'
import IndexView from '@/components/IndexView.vue'

import TestEmptyState from '@/components/EmptyState/TestEmptyState.vue'
import Datepicker from 'vuejs-datepicker'
import { statusDisplay } from '@/filters'
import { WorkspaceUsers } from './WorkspaceSettings.vue'

const defaultStatusOption = { value: null, label: 'All Statuses' }
const defaultAudienceOption = { value: null, label: 'All Audiences' }
const defaultTestTypeOption = { value: null, label: 'All Test Types' }
const defaultCreatorOption = { value: null, label: 'All Creators' }
const defaultTagOption: SelectOption[] = []

@Component({
  components: {
    Icon,
    TestTable,
    FilterBar,
    AddTestModal,
    Select,
    Button,
    IndexView,
    Card,
    TestCard,
    TestEmptyState,
    Datepicker,
    FilterSVG
  }
})

export default class TestIndex extends Vue {
  filter: TestFilter = {
    creators: defaultCreatorOption,
    statuses: defaultStatusOption,
    audiences: defaultAudienceOption,
    testTypes: defaultTestTypeOption,
    tags: defaultTagOption,
    date: null,
    searchTerm: null
  }

  showFilters: boolean = false
  showAddTestModal = false
  showTable: boolean = true
  workspaceUsers: WorkspaceUsers = []
  formattedUsers = {}
  creator = []
  createdTimeframe:{ from: Date | null, to: Date | null } = { from: null, to: null }
  completedTimeframe:{ from: Date | null, to: Date | null } = { from: null, to: null }

  filteredTests:Test[] = []

  mounted () {
    this.retrieveWorkspaceUsers()
  }

  get statusOptions () {
    const allStatuses = Object.values(TestStatus).sort()
    const statuses: SelectOption[] = allStatuses.map(status => {
      const displayStatus = statusDisplay(status)
      return { value: status, label: displayStatus }
    })
    statuses.unshift(defaultStatusOption)
    return statuses
  }

  get testTypeOptions () {
    const allTestTypes = Object.values(TestTypes)
    // Ignore some old legacy test types
    const ignoredTestTypes = [TestTypes.ProductDiscovery]
    const testTypes: SelectOption[] = allTestTypes
      .filter(status => !ignoredTestTypes.includes(status))
      .map(status => {
        return { value: status, label: status }
      })
    testTypes.unshift(defaultTestTypeOption)
    return testTypes
  }

  get audienceOptions () {
    const allAudiences:string[] = []
    this.tests.forEach(test => {
      if (typeof test.payload.segment === 'string') {
        allAudiences.push(test.payload.segment)
      } else {
        allAudiences.push(test.payload.segment?.title ?? '')
      }
    })
    const audiences: SelectOption[] = uniq(allAudiences).sort().map(audience => {
      return { value: audience, label: audience }
    })
    audiences.unshift(defaultAudienceOption)
    return audiences
  }

  get creatorOptions () {
    const creators: SelectOption[] = []
    if (!this.workspaceUsers) return creators

    const userIds = this.sortedTestsByDate.reduce((ids, test) => {
      if (!ids.includes(test.created_by.id)) {
        ids.push(test.created_by.id)
      }
      return ids
    }, [] as string[])

    userIds.forEach(id => {
      const creator = this.workspaceUsers.find(user => user.id === id)
      if (creator) {
        let fullName = ''
        const firstName = creator.payload.firstname
        const lastName = creator.payload.lastname
        if (firstName) {
          fullName += firstName
        }
        if (lastName) {
          fullName += ' ' + lastName
        }
        creators.push({ value: id, label: fullName })
      }
    })

    creators.unshift(defaultCreatorOption)
    return creators
  }

  get tagOptions () {
    const allTags: SelectOption[] = []

    this.tests.reduce((tags: Set<string>, test) => {
      if (test.payload?.tags) {
        const testTags: string[] = test.payload.tags.split(',')
        testTags.forEach(tag => tags.add(tag))
      }
      return tags
    }, new Set<string>()).forEach(tag => {
      const option: SelectOption = {
        value: tag,
        label: tag
      }

      allTags.push(option)
    })

    return allTags
  }

  get workspaceId () {
    return this.$route.params.workspaceId
  }

  get tests () {
    return store.getters.workspace.tests
  }

  get sortedTestsByDate () {
    return orderBy(this.tests, ['updatedAt'], ['desc'])
  }

  get filterTests () {
    const normalizedSearchTerm = this.filter.searchTerm && normalizeSearchTerm(this.filter.searchTerm)
    this.filteredTests = this.sortedTestsByDate.filter(test => {
      if (normalizedSearchTerm && !test.payload.title?.toLowerCase().includes(normalizedSearchTerm)) {
        return false
      }

      if (this.filter.date && this.createdTimeframe.from && this.createdTimeframe.to) {
        // remove timezone from date selected and set to beginning of day
        const fromDate = new Date(this.createdTimeframe.from.setHours(0, 0, 0, 0)).getTime() / 1000

        // set to end of day
        const toDate = new Date(this.createdTimeframe.to.setHours(23, 59, 59, 999)).getTime() / 1000

        if (!((fromDate <= test.createdAt) && test.createdAt <= toDate)) {
          return false
        }
      }

      if (this.filter.date && this.completedTimeframe.from && this.completedTimeframe.to) {
        // remove timezone from date selected and set to beginning of day
        const fromDate = new Date(this.completedTimeframe.from.setHours(0, 0, 0, 0)).getTime() / 1000

        // set to end of day
        const toDate = new Date(this.completedTimeframe.to.setHours(23, 59, 59, 999)).getTime() / 1000

        if (!(test.payload.completedAt && (fromDate <= test.payload.completedAt) && test.payload.completedAt <= toDate)) {
          return false
        }
      }

      if (this.filter.statuses.value) {
        if (test.payload.status !== this.filter.statuses.value) {
          return false
        }
      }

      if (this.filter.creators.value) {
        if (test.created_by.id !== this.filter.creators.value) {
          return false
        }
      }

      if (this.filter.audiences.value) {
        if (typeof test.payload.segment === 'string') {
          return test.payload.segment === this.filter.audiences.value
        } else {
          return test.payload.segment?.title === this.filter.audiences.value
        }
      }

      if (this.filter.testTypes.value) {
        if (test.payload.testType !== this.filter.testTypes.value) {
          return false
        }
      }

      if (this.filter.tags.length > 0) {
        if (!test.payload.tags) return false

        const filteredTags = this.filter.tags.map(tag => tag.value)
        const testTags = test.payload.tags.split(',')

        if (intersection(filteredTags, testTags).length === 0) return false
      }

      return true
    })

    return this.filteredTests
  }

  toggleFilter () {
    if (this.showFilters) {
      this.showFilters = false
    } else {
      this.showFilters = true
    }
  }

  clearFilter () {
    // clear all pre-existing filters
    this.filter.audiences = defaultAudienceOption
    this.filter.statuses = defaultStatusOption
    this.filter.creators = defaultCreatorOption
    this.filter.testTypes = defaultTestTypeOption
    this.filter.tags = defaultTagOption
    this.createdTimeframe.from = null
    this.createdTimeframe.to = null
    this.completedTimeframe.from = null
    this.completedTimeframe.to = null
  }

  selectOption (newOption: SelectOption, type: 'statuses' | 'creators' | 'audiences' | 'testTypes') {
    this.filter[type] = newOption
  }

  selectMultipleOptions (newMultipleOptions: SelectOption[], type: 'tags') {
    this.filter[type] = newMultipleOptions
  }

  updateSearch (filter: { searchTerm: string }) {
    this.filter.searchTerm = filter.searchTerm
  }

  retrieveWorkspaceUsers () {
    api.getWorkspacePreferences(this.workspaceId)
      .then((res) => {
        this.workspaceUsers = res.workspaceUsers
      }).catch(error => {
        console.error(error)
      })
  }

  openModal () {
    this.showAddTestModal = true
  }

  triggerToggle () {
    this.showTable = !this.showTable
  }
}

